import { useEditorStore } from "components/Editor/useEditorStore";
import { useBranchEditor, useEditorFeature } from "features/editor/Feature";
import { useSetBranchProtection } from "hooks/api/useSetBranchProtection";
import { Stack, Switch, Button } from "prism-react";
import * as React from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { useAutoOpenPreview } from "hooks/useAutoOpenPreview";
import { useGitHubAppWindow } from "hooks/useGitHubAppWindow";
import { useGithubEventsSubscription } from "hooks/useGithubEventsSubscription";
import { SubscriptionsProvider } from "hooks/useSubscriptionsClient";

import { SettingItem } from "../common";

export const SettingsRepository: React.FC<{
  visible: boolean;
}> = () => {
  return (
    <Stack gap={5}>
      <SettingProtectBranch />
      <SubscriptionsProvider>
        <SettingGitHubApp />
      </SubscriptionsProvider>
      <PreviewsInBrowserTab />
    </Stack>
  );
};

export const SettingProtectBranch: React.FC<{
  title?: string;
  description?: string;
}> = ({
  title = "Protect current branch",
  description = "Prevent changes from being made in the current branch.",
}) => {
  const track = useAnalytics();
  const setBranchProtection = useSetBranchProtection();
  const [apiErrorMessage, setApiErrorMessage] = React.useState<string | null>(
    null,
  );
  const project = useBranchEditor();
  const { git } = useEditorStore();

  const onProtectedBranchChange = (newValue: boolean) => {
    try {
      setBranchProtection({ protected: newValue, branchId: project.id });
      setApiErrorMessage(null);

      track("procted_branch_toggle", { value: newValue });
    } catch (_) {
      setApiErrorMessage(
        "There was an error setting the protected status of this branch.",
      );
    }
  };

  const gitErrorMessage = git.status.conflicts
    ? "You need to fix your conflicts to change this setting."
    : git.status.localChanges
      ? "You need to commit your files to change this setting."
      : git.status.isMerging
        ? "You need to finish your merge to change this setting."
        : undefined;

  const errorMessage = apiErrorMessage || gitErrorMessage;

  return (
    <SettingItem
      description={description}
      error={errorMessage}
      title={title}
      titleTooltip="Every branch will be behind a unique URL"
    >
      <Switch
        aria-label="Protect current branch"
        defaultChecked={project.protectedBranch}
        disabled={
          git.status.localChanges ||
          git.status.conflicts ||
          git.status.isMerging
        }
        onCheckedChange={onProtectedBranchChange}
      />
    </SettingItem>
  );
};

export const SettingGitHubApp: React.FC = () => {
  const [_, dispatchProject] = useEditorFeature();
  const project = useBranchEditor();

  const { openNewWindow, resetWindowControl } = useGitHubAppWindow(
    "gh_app_install_from_settings",
  );

  const [{ data }] = useGithubEventsSubscription({
    owner: project.owner,
    repo: project.repo,
  });

  React.useEffect(
    function successGithubAppInstalled() {
      if (data?.githubEvents.action === "CREATED") {
        resetWindowControl();

        dispatchProject({ type: "SET_GITHUB_APP" });
      }
    },
    [data],
  );

  return (
    <SettingItem
      description="Get a live dev environment and a preview for every PR."
      title="Install GitHub App"
    >
      <Button
        disabled={project.githubAppInstalled}
        onClick={openNewWindow}
        variant="secondary"
      >
        {project.githubAppInstalled ? "Installed" : "Install app"}
      </Button>
    </SettingItem>
  );
};

const PreviewsInBrowserTab: React.FC = () => {
  const [autoOpenPreviews, setAutoOpenPreviews] = useAutoOpenPreview();

  return (
    <SettingItem
      description="Toggle opening previews automatically in the devtool panel for this project and device. When turned off open previews from the task or the devtools panel."
      title="Open previews automatically"
    >
      <Switch
        aria-label="Open previews automatically"
        defaultChecked={autoOpenPreviews}
        onCheckedChange={() => setAutoOpenPreviews(!autoOpenPreviews)}
      />
    </SettingItem>
  );
};
